/**
* Task Cache Utility
* 
* Provides IndexedDB-based caching for tasks to enable offline access
* and faster loading of large datasets.
* 
* Features:
* - Full sync: Complete cache replacement
* - Delta updates: Incremental updates for live rehydration
* - Deletion handling: Removes deleted tasks from cache
* - Batch operations: Optimized performance with batch writes
*/

import type { Task } from '@/db';
import { fetchAllTasksPaginated } from '@/db';
import {
  clearStore,
  putItem,
  getAllItems,
  getItem,
  countItems,
  deleteDb as deleteDatabase,
  isIndexedDBAvailable as checkIndexedDBAvailable,
} from './indexedDBHelpers';
import { logger } from './logger';
import { handleError } from '@/utils/errorHandler';
import { DB_NAME, DB_VERSION } from './cacheConstants';
const STORE_NAME = 'tasks';
const META_STORE_NAME = 'meta';
const STRUCTURE_STORE_NAME = 'structure';
const STRUCTURE_META_STORE_NAME = 'structureMeta';

// Module-level flag: disabled for the session if quota recovery fails
let indexedDBEnabled = true;

/**
 * Check whether IndexedDB caching is available and has not been
 * disabled due to an unrecoverable quota error this session.
 */
export function isIndexedDBAvailable(): boolean {
  return indexedDBEnabled && checkIndexedDBAvailable();
}

/** Detect QuotaExceededError from an unknown caught value */
function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'QuotaExceededError';
}

/**
 * Evict the oldest cached tasks to free IndexedDB space.
 * Deletes roughly 20 % of stored tasks ordered by `updatedAt` ascending.
 */
async function evictOldestTasks(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const allTasks = await getAllItems<Task & { id: string }>(store);

  // Sort oldest-updated first
  allTasks.sort((a, b) => {
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return aTime - bTime;
  });

  const evictCount = Math.max(1, Math.floor(allTasks.length * 0.2));
  const idsToEvict = allTasks.slice(0, evictCount).map((t) => t.id);

  await new Promise<void>((resolve, reject) => {
    const deleteTx = db.transaction([STORE_NAME], 'readwrite');
    const deleteStore = deleteTx.objectStore(STORE_NAME);
    deleteTx.oncomplete = () => { db.close(); resolve(); };
    deleteTx.onerror = () => { db.close(); reject(deleteTx.error); };
    for (const id of idsToEvict) {
      deleteStore.delete(id);
    }
  });
}

interface CacheMetadata {
  lastSyncTime: number;
  totalTasks: number;
  version: string;
  lastDeltaSyncTime?: number; // Timestamp of last delta update
}

// Initialize IndexedDB database
async function openDB(hasRetried = false): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = async () => {
      const db = request.result;

      // Self-heal: if a user has an older IndexedDB schema (e.g. after a deploy)
      // the DB can open but miss required stores, causing NotFoundError on transaction().
      const requiredStores = [
        STORE_NAME,
        META_STORE_NAME,
        STRUCTURE_STORE_NAME,
        STRUCTURE_META_STORE_NAME,
        'spaceItems',
      ];

      const missing = requiredStores.filter((s) => !db.objectStoreNames.contains(s));
      if (missing.length > 0) {
        db.close();
        if (hasRetried) {
          reject(new Error(`IndexedDB schema missing stores: ${missing.join(', ')}`));
          return;
        }
        try {
          await deleteDatabase(DB_NAME);
          const repaired = await openDB(true);
          resolve(repaired);
        } catch (e) {
          reject(e);
        }
        return;
      }

      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create tasks store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const taskStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        taskStore.createIndex('dateCreated', 'dateCreated', { unique: false });
        taskStore.createIndex('spaceId', 'spaceId', { unique: false });
      }

      // Create metadata store if it doesn't exist
      if (!db.objectStoreNames.contains(META_STORE_NAME)) {
        db.createObjectStore(META_STORE_NAME, { keyPath: 'key' });
      }

      // Create unified structure store (spaces, folders, lists)
      if (!db.objectStoreNames.contains(STRUCTURE_STORE_NAME)) {
        const structureStore = db.createObjectStore(STRUCTURE_STORE_NAME, { keyPath: 'id' });
        structureStore.createIndex('type', 'type', { unique: false });
        structureStore.createIndex('name', 'name', { unique: false });
        structureStore.createIndex('project_id', 'project_id', { unique: false });
        structureStore.createIndex('folder_id', 'folder_id', { unique: false });
      }

      if (!db.objectStoreNames.contains(STRUCTURE_META_STORE_NAME)) {
        db.createObjectStore(STRUCTURE_META_STORE_NAME, { keyPath: 'key' });
      }

      // Create space items store (added in version 3)
      if (!db.objectStoreNames.contains('spaceItems')) {
        const spaceItemsStore = db.createObjectStore('spaceItems', { keyPath: 'spaceId' });
        spaceItemsStore.createIndex('spaceId', 'spaceId', { unique: true });
      }
    };
  });
}

/**
* Store tasks in the cache (full sync - replaces all existing tasks)
* Uses batch operations for better performance
*/
export async function cacheTasks(tasks: Array<Task & { id: string }>, _isRetry = false): Promise<void> {
  if (!isIndexedDBAvailable()) return;

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME, META_STORE_NAME], 'readwrite');
    const taskStore = transaction.objectStore(STORE_NAME);
    const metaStore = transaction.objectStore(META_STORE_NAME);

    // Clear existing tasks
    await clearStore(taskStore);

    // Batch write tasks for better performance
    const BATCH_SIZE = 100;
    for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
      const batch = tasks.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(task => putItem(taskStore, task)));
    }

    // Update metadata
    const metadata: CacheMetadata = {
      lastSyncTime: Date.now(),
      totalTasks: tasks.length,
      version: '2.0',
    };
    await putItem(metaStore, { key: 'metadata', ...metadata });

    db.close();
  } catch (error) {
    if (isQuotaError(error)) {
      handleError(error, {
        level: 'warning',
        message: 'IndexedDB quota exceeded while caching tasks',
        operation: 'cacheTasks',
      });
      if (!_isRetry) {
        try {
          await evictOldestTasks();
          return cacheTasks(tasks, true);
        } catch {
          // Eviction or retry failed – disable for session
        }
      }
      indexedDBEnabled = false;
      logger.warn('[TaskCache] IndexedDB disabled for this session after quota failure');
      return;
    }
    logger.error('Failed to cache tasks:', error);
    throw error;
  }
}

/**
* Apply delta updates to cache (for live rehydration)
* Updates existing tasks, adds new ones, and optionally removes deleted ones
* 
* @param tasks - Tasks to update/add
* @param deletedIds - Optional array of task IDs to remove from cache
*/
export async function applyDeltaUpdates(
  tasks: Array<Task & { id: string }>,
  deletedIds?: string[],
  _isRetry = false,
): Promise<void> {
  if (!isIndexedDBAvailable()) return;

  try {
    const db = await openDB();

    // Get current metadata first (before starting the transaction operations)
    const currentMetadata = await getCacheMetadata();

    // After transaction completes, get actual count to update metadata accurately
    const actualCount = await getCachedTaskCount();

    // Update metadata with delta sync time and accurate count
    const metadata: CacheMetadata = {
      lastSyncTime: currentMetadata?.lastSyncTime ?? Date.now(),
      lastDeltaSyncTime: Date.now(),
      totalTasks: actualCount, // Use actual count instead of estimate
      version: '2.0',
    };

    // Wrap all operations in a single transaction promise
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME, META_STORE_NAME], 'readwrite');
      const taskStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE_NAME);

      const totalOperations = tasks.length + (deletedIds?.length ?? 0) + 1; // +1 for metadata
      let hasError = false;

      const checkComplete = () => {
        // Don't resolve here - wait for transaction.oncomplete
      };

      transaction.onerror = () => {
        hasError = true;
        db.close();
        reject(transaction.error);
      };

      transaction.oncomplete = async () => {
        // Update metadata with accurate count after transaction completes
        try {
          const actualCount = await getCachedTaskCount();
          const finalMetadata: CacheMetadata = {
            lastSyncTime: currentMetadata?.lastSyncTime ?? Date.now(),
            lastDeltaSyncTime: Date.now(),
            totalTasks: actualCount,
            version: '2.0',
          };

          // Update metadata in a new transaction
          const metaTransaction = db.transaction([META_STORE_NAME], 'readwrite');
          const metaStore = metaTransaction.objectStore(META_STORE_NAME);
          await putItem(metaStore, { key: 'metadata', ...finalMetadata });
        } catch (error) {
          logger.error('Failed to update metadata after delta update:', error);
        }

        db.close();
        if (!hasError) {
          resolve();
        }
      };

      // Batch update/add tasks
      if (tasks.length > 0) {
        tasks.forEach((task) => {
          const putRequest = taskStore.put(task);
          putRequest.onsuccess = () => checkComplete();
          putRequest.onerror = () => {
            hasError = true;
            reject(putRequest.error);
          };
        });
      }

      // Remove deleted tasks if provided
      if (deletedIds && deletedIds.length > 0) {
        deletedIds.forEach((id) => {
          const deleteRequest = taskStore.delete(id);
          deleteRequest.onsuccess = () => checkComplete();
          deleteRequest.onerror = () => {
            hasError = true;
            reject(deleteRequest.error);
          };
        });
      }

      // Update metadata (always do this)
      const putRequest = metaStore.put({ key: 'metadata', ...metadata });
      putRequest.onsuccess = () => checkComplete();
      putRequest.onerror = () => {
        hasError = true;
        reject(putRequest.error);
      };

      // If no operations were queued, resolve immediately
      if (totalOperations === 0) {
        resolve();
      }
    });
  } catch (error) {
    if (isQuotaError(error)) {
      handleError(error, {
        level: 'warning',
        message: 'IndexedDB quota exceeded during delta update',
        operation: 'applyDeltaUpdates',
      });
      if (!_isRetry) {
        try {
          await evictOldestTasks();
          return applyDeltaUpdates(tasks, deletedIds, true);
        } catch {
          // Eviction or retry failed – disable for session
        }
      }
      indexedDBEnabled = false;
      logger.warn('[TaskCache] IndexedDB disabled for this session after quota failure');
      return;
    }
    logger.error('Failed to apply delta updates:', error);
    throw error;
  }
}

/**
* Remove tasks from cache by their IDs
*/
export async function removeTasksFromCache(taskIds: string[]): Promise<void> {
  if (taskIds.length === 0) return;
  if (!isIndexedDBAvailable()) return;

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      // Keep everything in ONE transaction; don't await other async helpers here.
      const transaction = db.transaction([STORE_NAME, META_STORE_NAME], 'readwrite');
      const taskStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE_NAME);

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        const err = transaction.error;
        db.close();
        reject(err);
      };

      // Queue deletes
      for (const id of taskIds) {
        taskStore.delete(id);
      }

      // Count after deletes (this request is queued after deletes)
      const countReq = taskStore.count();
      countReq.onsuccess = () => {
        const totalTasks = countReq.result;

        // Read existing metadata (optional) then write updated metadata
        const metaGet = metaStore.get('metadata');
        metaGet.onsuccess = () => {
          const prev = metaGet.result || {};
          const lastSyncTime = typeof prev.lastSyncTime === 'number' ? prev.lastSyncTime : Date.now();
          const lastDeltaSyncTime = typeof prev.lastDeltaSyncTime === 'number' ? prev.lastDeltaSyncTime : undefined;
          metaStore.put({
            key: 'metadata',
            lastSyncTime,
            lastDeltaSyncTime,
            totalTasks,
            version: '2.0',
          });
        };
        metaGet.onerror = () => {
          metaStore.put({
            key: 'metadata',
            lastSyncTime: Date.now(),
            totalTasks,
            version: '2.0',
          });
        };
      };
    });
  } catch (error) {
    if (isQuotaError(error)) {
      handleError(error, {
        level: 'warning',
        message: 'IndexedDB quota exceeded during task removal',
        operation: 'removeTasksFromCache',
      });
      // Removal shouldn't normally hit quota, but if it does just warn
      indexedDBEnabled = false;
      logger.warn('[TaskCache] IndexedDB disabled for this session after quota failure');
      return;
    }
    logger.error('Failed to remove tasks from cache:', error);
    throw error;
  }
}

/**
* Get all cached tasks
*/
export async function getCachedTasks(): Promise<Array<Task & { id: string }>> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const taskStore = transaction.objectStore(STORE_NAME);
    const tasks = await getAllItems<Task & { id: string }>(taskStore);
    db.close();
    // Sort by dateCreated descending (newest first)
    tasks.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));
    return tasks;
  } catch (error) {
    logger.error('Failed to get cached tasks:', error);
    return [];
  }
}

/**
* Get cached task count
*/
export async function getCachedTaskCount(): Promise<number> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const taskStore = transaction.objectStore(STORE_NAME);
    const count = await countItems(taskStore);
    db.close();
    return count;
  } catch (error) {
    logger.error('Failed to get cached task count:', error);
    return 0;
  }
}

/**
* Get cache metadata
*/
export async function getCacheMetadata(): Promise<CacheMetadata | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction([META_STORE_NAME], 'readonly');
    const metaStore = transaction.objectStore(META_STORE_NAME);
    const result = await getItem<{ key: string } & CacheMetadata>(metaStore, 'metadata');
    db.close();
    if (result) {
      const { key: _key, ...metadata } = result;
      return metadata;
    }
    return null;
  } catch (error) {
    logger.error('Failed to get cache metadata:', error);
    return null;
  }
}

/**
* Clear the cache
*/
export async function clearCache(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME, META_STORE_NAME], 'readwrite');
    const taskStore = transaction.objectStore(STORE_NAME);
    const metaStore = transaction.objectStore(META_STORE_NAME);

    await Promise.all([
      clearStore(taskStore),
      clearStore(metaStore),
    ]);

    db.close();
  } catch (error) {
    logger.error('Failed to clear cache:', error);
    throw error;
  }
}

/**
* Delete the entire IndexedDB database (for logout cleanup).
* Unlike clearCache(), this removes all stores including structure and spaceItems.
*/
export async function deleteAllCachedData(): Promise<void> {
  try {
    await deleteDatabase(DB_NAME);
  } catch (error) {
    logger.error('Failed to delete IndexedDB database:', error);
    throw error;
  }
}

// isIndexedDBAvailable is now defined at the top of this module
// with quota-awareness (see above).

/**
* Sync all tasks from Supabase to cache
* This function delegates fetching to the db layer and handles dedup, sort, and caching
*/
export async function syncAllTasksFromSupabase(
  onProgress?: (progress: { current: number; total: number; spaceName: string }) => void
): Promise<Array<Task & { id: string }>> {
  try {
    const tasks = await fetchAllTasksPaginated(
      onProgress ? (progress) => onProgress({ ...progress, spaceName: 'All tasks' }) : undefined
    );

    // Dedup
    const taskIdsSet = new Set<string>();
    let duplicateCount = 0;
    const uniqueTasks = tasks.filter((task) => {
      if (!task.id || taskIdsSet.has(task.id)) {
        if (task.id) duplicateCount++;
        return false;
      }
      taskIdsSet.add(task.id);
      return true;
    });

    // Sort by dateCreated descending (newest first)
    uniqueTasks.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));

    if (duplicateCount > 0) {
      logger.warn(`[Cache] Found ${duplicateCount} duplicate tasks during sync`);
    }

    // Store in cache
    await cacheTasks(uniqueTasks as Array<Task & { id: string }>);

    return uniqueTasks as Array<Task & { id: string }>;
  } catch (error) {
    logger.error('Failed to sync tasks from Supabase:', error);
    throw error;
  }
}
